import { Type, Static } from '@sinclair/typebox';
import { SocialHandlesSchema } from '../../shared/social-handle.schema';
import { minimalUserSchema } from '../../shared/user/user.schema';

export const ContactSchema = Type.Object({
  object: Type.Literal('Contact', { description: 'Object type identifier, always "Contact".' }),
  id: Type.String({ description: 'Unique identifier of the contact.' }),
  user: Type.Optional(
    Type.Object(minimalUserSchema, {
      description: 'For Messaging/Social Providers, the underlying provider user represented by this contact.',
    }),
  ),
  public_picture_url: Type.Optional(
    Type.String({ format: 'uri', description: 'Public URL of the profile picture of the contact.' }),
  ),
  private_picture_download_url: Type.Optional(
    Type.String({
      format: 'uri',
      description: 'Private URL to download the profile picture of the contact. This URL requires authentication.',
    }),
  ),
  picture_url: Type.Optional(
    Type.String({
      deprecated: true,
      description: 'URL of the profile picture of the contact. Use public_picture_url or private_picture_download_url instead.',
    }),
  ),
  first_name: Type.Optional(Type.String({ description: 'First name of the contact.' })),
  middle_name: Type.Optional(Type.String({ description: 'Middle name of the contact.' })),
  last_name: Type.Optional(Type.String({ description: 'Last name of the contact.' })),
  display_name: Type.Optional(
    Type.String({
      description: 'Display name of the contact. This is used if first/middle/last name cannot be determined.',
    }),
  ),
  suffix: Type.Optional(Type.String({ description: 'Name suffix (e.g. Jr., Sr., PhD).' })),
  birth_date: Type.Optional(Type.String({ description: 'Birth date of the user.' })),

  company_name: Type.Optional(Type.String({ description: 'Name of the company the contact works for.' })),
  job_title: Type.Optional(Type.String({ description: 'Job title or position of the contact.' })),
  manager_name: Type.Optional(Type.String({ description: "Name of the contact's manager." })),
  office_location: Type.Optional(Type.String({ description: 'Office location or building of the contact.' })),

  notes: Type.Optional(Type.String({ description: 'Free-text notes associated with the contact.' })),

  emails: Type.Optional(Type.Array(Type.String(), { description: 'List of user email addresses.' })),
  phone_numbers: Type.Optional(Type.Array(Type.String(), { description: 'List of user phone numbers.' })),
  social_handles: Type.Optional(SocialHandlesSchema),
  addresses: Type.Optional(
    Type.Array(
      Type.Object({
        city: Type.String({ description: 'City of the physical address.' }),
        country: Type.String({ description: 'Country of the physical address.' }),
        postal_code: Type.String({ description: 'Postal or ZIP code of the physical address.' }),
        state: Type.String({ description: 'State or province of the physical address.' }),
        street_address: Type.String({ description: 'Street address line.' }),
        type: Type.String({ description: 'Type of address (e.g. personal, business, other).' }),
      }),
      { description: 'List of physical addresses associated with the contact.' },
    ),
  ),
  websites: Type.Optional(Type.Array(Type.String(), { description: 'List of user websites.' })),
  groups: Type.Optional(
    Type.Array(
      Type.Object({
        id: Type.String({ description: 'Identifier of a group the contact belongs to.' }),
      }),
      { description: 'List of groups the contact belongs to.' },
    ),
  ),
});

export type Contacts = Static<typeof ContactSchema>;
